import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MutationHandlingServiceV2, QueryHandlingService } from '@akebono/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { combineLatest, Observable, of, Subject, timer } from 'rxjs';
import {
  distinctUntilChanged,
  first,
  map,
  shareReplay,
  startWith,
  takeUntil,
} from 'rxjs/operators';
import { validateAllFormFields } from 'src/app/const';
import {
  Lot,
  LotSource,
  LotTypeEnum,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';
import {
  RequestPhotoFromAuctionGQL,
  ServiceRequestAuctionLotPriceGQL,
} from 'src/app/modules/graphql/service/graphql-shared-familiar-service';
import { LotWatchRequestInspectionData } from 'src/app/services/auction-video-watch/types/lot-watch-request-inspection-data.type';
import { LotShareService } from 'src/app/services/lot-share.service';

import { LotWatchRequestTimeLimitService } from '../../../../../services/lot-watch-request-time-limit.service';
import { AuctionData } from 'src/app/modules/graphql/service/graphql-directory-service';

@Component({
  selector: 'app-lot-watch-request-form-modal',
  templateUrl: './lot-watch-request-form-modal.component.html',
  styleUrls: ['./lot-watch-request-form-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotWatchRequestFormModalComponent implements OnInit, OnDestroy {
  @Input() lot: Lot;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() lotShared: EventEmitter<void>;
  @Input() inspectionData: LotWatchRequestInspectionData;
  @Input() auctionData: AuctionData;

  loading$: Observable<boolean>;
  creating$ = of(false);

  lotLink$: Observable<string>;
  price$: Observable<number>;
  timeLimitReached$: Observable<boolean>;

  form: FormGroup;
  commentLimit = 300;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly modal: NzModalRef,
    private readonly fb: FormBuilder,
    private readonly qhs: QueryHandlingService,
    private readonly mhs: MutationHandlingServiceV2,
    private readonly lotShareService: LotShareService,
    private readonly requestPhotoFromAuctionGQL: RequestPhotoFromAuctionGQL,
    private readonly lotWatchRequestTimeLimitService: LotWatchRequestTimeLimitService,
    private readonly serviceRequestAuctionLotPriceGQL: ServiceRequestAuctionLotPriceGQL,
  ) {
    this.form = this.fb.group({
      comment: ['', [Validators.required, Validators.maxLength(this.commentLimit)]],
    });
  }

  ngOnInit(): void {
    this.initTimeLimitReached();
    const shareLotResult = this.lotShareService.share(this.lot, this.lotType, this.lotSource, {
      useFullDomain: false,
    });
    this.lotLink$ = shareLotResult.link$.pipe(shareReplay(1));
    const priceRef = this.qhs.fetch(
      this.serviceRequestAuctionLotPriceGQL,
      {
        type: this.auctionData.inspectionId!,
        lotId: this.lot.id,
        lotSource: this.lotSource,
        lotSubgroup: this.lotType.toString(),
      },
      'network-only',
    );
    this.price$ = priceRef.data.pipe(
      map((res) => res?.serviceRequestAuctionLotPrice ?? 0),
      startWith(0),
    );
    this.loading$ = combineLatest([priceRef.loading, shareLotResult.loading$]).pipe(
      map((loadings) => loadings.some(Boolean)),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  initTimeLimitReached(): void {
    this.timeLimitReached$ = timer(0, 1000).pipe(
      map(() => {
        const inspectionAvailable = this.lotWatchRequestTimeLimitService.isInspectionAvailable(
          this.lot,
          this.auctionData,
        );
        return !inspectionAvailable;
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  async createRequest(isOrder: boolean = false): Promise<void> {
    if (!isOrder) {
      validateAllFormFields(this.form);

      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
    }

    const lotLink = await this.lotLink$.toPromise();
    const mutationRef = this.mhs.mutate(
      this.requestPhotoFromAuctionGQL,
      {
        input: {
          lotLink,
          lotId: this.lot.id,
          lotSource: this.lotSource,
          lotSubgroup: this.lotType.toString(),
          type: this.auctionData.inspectionId!,
          lotData: `${this.lot.code} - ${this.lot.auctionName}`,
          comment: this.form.value.comment.trim(),
          dueDate: this.lot.auctionDatetime,
        },
      },
      {
        refetch: true,
      },
    );

    this.creating$ = mutationRef.loading;
    mutationRef.data.pipe(first(), takeUntil(this.destroy$)).subscribe(() => this.close());
  }

  computeLength = (value: string): number => (value || '').trim().length;

  close(): void {
    this.modal.destroy();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
